export const daysInReportMonth = (year, month) => {
  const y = Number(year);
  const m = Number(month);
  if (!y || !m) return 0;
  return new Date(y, m, 0).getDate();
};

export const availableDaysOverPoolDisplay = (unitAvailableDays, totalAvailableDays) => {
  if (unitAvailableDays == null || unitAvailableDays === '') return '';
  const unitDays = Number(unitAvailableDays) || 0;
  const poolDays = Number(totalAvailableDays) > 0 ? Number(totalAvailableDays) : unitDays;
  return `${unitDays} / ${poolDays}`;
};

export const availableDaysOverMonthDisplay = availableDaysOverPoolDisplay;

export const shouldShowPayoutAmount = (year, month, now) => {
  const current = now || new Date();
  const y = Number(year);
  const m = Number(month);
  if (!y || !m) return false;
  return !(y > current.getFullYear() || (y === current.getFullYear() && m > current.getMonth() + 1));
};

const EXPENSE_SCALAR_KEYS = [
  'cleaning',
  'electricity',
  'water',
  'internet',
  'laundry',
  'service',
  'checkIn',
  'checkOut',
  'indahWater',
  'fireInsurance',
  'strFees',
  'quitRent',
  'waterDispenser',
];

const EXPENSE_SETTING_KEY = {
  cleaning: 'cleaningFee',
  electricity: 'electricity',
  water: 'water',
  internet: 'internet',
  laundry: 'laundryFee',
  service: 'serviceFee',
  checkIn: 'checkInFee',
  checkOut: 'checkOutFee',
  others: 'otherFee',
  toiletry: 'toiletryFee',
};

const normalizeShare = value => {
  if (value === 'host' || value === 'owner' || value === 'shared') return value;
  return 'shared';
};

export const shareFromExpensesSetting = (setting, expenseKey) => {
  const s = setting || {};
  const settingKey = EXPENSE_SETTING_KEY[expenseKey] || expenseKey;
  if (s[settingKey]) return normalizeShare(s[settingKey]);
  if (s[expenseKey]) return normalizeShare(s[expenseKey]);
  return 'shared';
};

const unwrapExpenseBlob = expenses => {
  if (!expenses || typeof expenses !== 'object') return {};
  const nested = expenses.expenses;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested;
  return expenses;
};

const pushLineItems = (items, rows, share) => {
  (rows || []).forEach((row, i) => {
    const amount = Number(row && row.amount) || 0;
    if (!amount) return;
    items.push({ name: (row && row.label) || `other-${i}`, amount, share });
  });
};

const rollupByUnitExpenses = src => {
  const byUnit = Array.isArray(src && src.byUnit) ? src.byUnit : [];
  if (!byUnit.length) return src;
  const rolled = Object.assign({}, src);
  EXPENSE_SCALAR_KEYS.forEach(key => {
    const fromUnits = byUnit.reduce((sum, row) => sum + (Number(row && row[key]) || 0), 0);
    rolled[key] = fromUnits > 0 ? fromUnits : Number(src[key]) || 0;
  });
  rolled.othersByHost = byUnit.reduce(
    (acc, row) => acc.concat((row && row.othersByHost) || []),
    Array.isArray(src.othersByHost) ? src.othersByHost.slice() : []
  );
  rolled.othersByOwner = byUnit.reduce(
    (acc, row) => acc.concat((row && row.othersByOwner) || []),
    Array.isArray(src.othersByOwner) ? src.othersByOwner.slice() : []
  );
  rolled.toiletry = byUnit.reduce(
    (acc, row) => acc.concat((row && row.toiletry) || []),
    Array.isArray(src.toiletry) ? src.toiletry.slice() : []
  );
  const othersLines =
    (rolled.othersByHost || []).reduce((sum, row) => sum + (Number(row && row.amount) || 0), 0) +
    (rolled.othersByOwner || []).reduce((sum, row) => sum + (Number(row && row.amount) || 0), 0);
  rolled.others = othersLines > 0 ? othersLines : Number(src.others) || 0;
  return rolled;
};

export const periodExpenseItems = items =>
  (items || []).filter(item => normalizeShare(item && item.share) !== 'owner');

export const sumExpenseAmounts = items =>
  (items || []).reduce((sum, item) => sum + (Number(item && item.amount) || 0), 0);

export const expenseItemsFromReport = (expenses, options) => {
  const opts = options || {};
  const setting = opts.expensesSetting || {};
  let src = unwrapExpenseBlob(expenses);
  const byUnit = src.byUnit || [];
  if (byUnit.length) src = rollupByUnitExpenses(src);
  const items = [];
  EXPENSE_SCALAR_KEYS.forEach(key => {
    const amount = Number(src[key]) || 0;
    if (amount) items.push({ name: key, amount, share: shareFromExpensesSetting(setting, key) });
  });
  const othersBefore = items.length;
  pushLineItems(items, src.othersByHost, 'host');
  pushLineItems(items, src.othersByOwner, 'owner');
  pushLineItems(items, src.toiletry, shareFromExpensesSetting(setting, 'toiletry'));
  const hasOtherLines = items.length > othersBefore;
  if (!hasOtherLines && Number(src.others)) {
    items.push({ name: 'others', amount: Number(src.others), share: shareFromExpensesSetting(setting, 'others') });
  }
  return items;
};

const todp = (num, precision = 2) => {
  const n = Number(num);
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** precision;
  return Math.round(n * factor) / factor;
};

export const borneExpensesFromItems = (items, totalExpenses) => {
  const borne = { host: 0, owner: 0, shared: 0 };
  (items || []).forEach(item => {
    const amount = Number(item && item.amount) || 0;
    const share = normalizeShare(item && item.share);
    borne[share] += amount;
  });
  const itemTotal = borne.host + borne.owner + borne.shared;
  if (!itemTotal && Number(totalExpenses)) {
    borne.shared = Number(totalExpenses) || 0;
  }
  return borne;
};

/** Host/owner exclusive lines stay 100%. Shared lines split by owner share %. */
export const splitBorneExpensesBySharePercentage = (borne, ownerSharePercentage) => {
  const ownerPct = Math.min(100, Math.max(0, Number(ownerSharePercentage) || 0));
  const hostExclusive = Number(borne && borne.host) || 0;
  const ownerExclusive = Number(borne && borne.owner) || 0;
  const shared = Number(borne && borne.shared) || 0;
  const ownerOfShared = todp(shared * (ownerPct / 100));
  const hostOfShared = todp(shared - ownerOfShared);
  return {
    host: todp(hostExclusive + hostOfShared),
    owner: todp(ownerExclusive + ownerOfShared),
    shared: todp(shared),
  };
};

/** Period "Borne by" lines ignore owner-exclusive expenses (those show under Owner Expenses). */
export const periodBorneExpensesByShare = (borne, ownerSharePercentage) =>
  splitBorneExpensesBySharePercentage(
    {
      host: Number(borne && borne.host) || 0,
      owner: 0,
      shared: Number(borne && borne.shared) || 0,
    },
    ownerSharePercentage
  );

const toYmd = value => {
  if (!value) return '1970-01-01';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const s = String(value);
    return s.length >= 10 ? s.slice(0, 10) : '1970-01-01';
  }
  return d.toISOString().slice(0, 10);
};

export const calculateOtherCharges = reservationCharges => {
  const chargesDisplayed = ['total', 'rental', 'extraGuest', 'cleaning'];
  const charges = reservationCharges || {};
  return Object.keys(charges).reduce((total, key) => {
    if (chargesDisplayed.indexOf(key) >= 0) return total;
    return total + (Number(charges[key]) || 0);
  }, 0);
};

export const mapReportToPayoutDetail = card => {
  const src = card || {};
  const reservations = (src.reservations || []).map((c, i) => ({
    id: c.reservationId || c.code || String(i),
    startDate: toYmd(c.checkInDate),
    endDate: toYmd(c.checkOutDate),
    numberOfPax: Number(c.numberOfPax) || 0,
    source: c.platform || c.bookingType || '-',
    platform: c.platform || '',
    status: c.status || 'Confirmed',
    charges: {
      total: Number(c.amount) || 0,
      rental: Number(c.grossAmount != null ? c.grossAmount : c.amount) || 0,
      cleaning: Number(c.cleaningFee) || 0,
      extraGuest: 0,
      stripeCharges: 0,
      addOnAmount: Number(c.addOnAmount) || 0,
      otherAmount: Number(c.otherAmount) || 0,
      platformFee: Number(c.platformFee) || 0,
    },
  }));
  const totalExpenses = Number(src.totalExpenses) || 0;
  const payout = Number(src.amountTransferToOwner) || 0;
  const netProfit = Number(src.netProfit != null ? src.netProfit : src.payoutToOwnerPerUnit) || 0;
  const expenseItems = src.expenseItems || [];
  const installmentFromItems = (src.installmentItems || []).reduce(
    (sum, item) => sum + (Number(item && item.amount) || 0),
    0
  );
  const storedInstallment = Number(src.installmentDeducted);
  const installmentDeducted = Math.max(
    Number.isFinite(storedInstallment) ? storedInstallment : 0,
    installmentFromItems
  );
  const expenseBuckets = borneExpensesFromItems(expenseItems, totalExpenses);
  const periodExpenses = periodExpenseItems(expenseItems);
  const waterfall = Number(src.ownerShare)
    ? buildOwnerPayoutWaterfall({
        totalRevenue: Number(src.totalRevenue) || 0,
        totalExpenses,
        ownerSharePercentage: src.ownerShare,
        unitCount: src.packageUnitCount || (src.servicePackage && src.servicePackage.numberOfUnits),
        unitAvailableDays: src.unitAvailableDays,
        totalAvailableDays: src.totalAvailableDays,
        groupingType: src.groupingType,
        installmentDeducted,
        shareType: src.shareType || (src.servicePackage && src.servicePackage.shareType) || 'share-by-profit',
        expenseBuckets,
      })
    : null;
  const unitSqft = src.unitSqft != null && src.unitSqft !== '' ? Number(src.unitSqft) || 0 : 0;
  const totalPoolSqft =
    src.totalPoolSqft != null && src.totalPoolSqft !== '' ? Number(src.totalPoolSqft) || 0 : 0;
  return {
    property: { name: src.propertyName || '-', roomType: '' },
    unit: { name: src.unitName || '-', currency: src.currency || 'MYR' },
    protection: src.protection || {},
    reservations,
    expenses: periodExpenses,
    totalExpenses: sumExpenseAmounts(periodExpenses),
    totalNetRevenue: Number(src.totalRevenue) || 0,
    totalRevenue: Number(src.totalRevenue) || 0,
    sharedExpenses: waterfall ? waterfall.sharedExpenses : Number(expenseBuckets.shared) || 0,
    totalProfit: waterfall ? waterfall.totalProfit : undefined,
    ownerShare: Number(src.ownerShare) || 0,
    ownerPerUnitProfit: waterfall ? waterfall.ownerPerUnitProfit : undefined,
    ownerExclusiveExpenses: waterfall ? waterfall.ownerExclusiveExpenses : Number(expenseBuckets.owner) || 0,
    nettProfit: waterfall ? waterfall.youEarn : payout,
    nettOwnersProfit: waterfall ? waterfall.nettOwnersProfit : netProfit,
    perUnitRevenue: waterfall ? waterfall.perUnitRevenue : payout,
    youEarn: waterfall ? waterfall.youEarn : payout,
    ownersRevenue: waterfall ? waterfall.ownersRevenue : undefined,
    ownerBorneExpenses: waterfall ? waterfall.ownerBorneExpenses : undefined,
    hasOwnerWaterfall: !!waterfall,
    borneExpenses: periodBorneExpensesByShare(expenseBuckets, src.ownerShare),
    installmentItems: src.installmentItems || [],
    freeStay: src.freeStay || { used: 0, balance: 0 },
    unitAvailableDays: Number(src.unitAvailableDays) || 0,
    totalAvailableDays: Number(src.totalAvailableDays) || 0,
    unitSqft,
    totalPoolSqft,
    shareProfitBySqft: !!src.shareProfitBySqft,
    monthDays: daysInReportMonth(src.year, src.month),
    availableDaysDisplay: availableDaysOverPoolDisplay(src.unitAvailableDays, src.totalAvailableDays),
    servicePackage: {
      name: src.packageName || '-',
      startDay: Number(src.startDay) || 1,
      numberOfUnits: Number(src.packageUnitCount) || 1,
      roomSizeInSqFt: src.unitSqft != null ? src.unitSqft : '-',
      sqFts: [],
      splitBySqft: !!src.shareProfitBySqft,
      calculations: [],
    },
  };
};

/**
 * Total Expenses = shared lines only. Owner lines come off after the split.
 */
export const buildOwnerPayoutWaterfall = ({
  totalRevenue,
  totalExpenses,
  ownerSharePercentage,
  unitCount,
  unitAvailableDays,
  totalAvailableDays,
  groupingType,
  installmentDeducted,
  shareType,
  expenseBuckets,
} = {}) => {
  const ownerPct = Math.min(100, Math.max(0, Number(ownerSharePercentage) || 0)) / 100;
  const revenue = Number(totalRevenue) || 0;
  const expenses = Number(totalExpenses) || 0;
  const buckets = expenseBuckets || { host: 0, owner: 0, shared: expenses };
  const sharedE = Number(buckets.shared) || 0;
  const ownerE = Number(buckets.owner) || 0;
  const shareByRevenue = shareType === 'share-by-revenue';
  const totalProfit = todp(revenue - sharedE);
  const ownersRevenue = shareByRevenue
    ? todp(revenue * ownerPct)
    : todp(totalProfit * ownerPct);
  const ownerBorneExpenses = todp(ownerE + sharedE * ownerPct);
  const nettOwnersProfit = shareByRevenue
    ? todp(ownersRevenue - ownerBorneExpenses)
    : todp(ownersRevenue - ownerE);
  const units = Math.max(1, Number(unitCount) || 1);
  const unitDays = Number(unitAvailableDays) || 0;
  const poolDays = Number(totalAvailableDays) || 0;
  const singleUnit = groupingType === 'individual' || units <= 1;
  const ratio = !singleUnit && poolDays > 0 ? unitDays / poolDays : !singleUnit ? 1 / units : 1;
  const ownerPerUnitProfit = todp(ownersRevenue * ratio);
  const ownerExclusiveExpenses = todp(ownerE * ratio);
  const perUnitRevenue = todp(nettOwnersProfit * ratio);
  const youEarn = todp(perUnitRevenue - (Number(installmentDeducted) || 0));
  return {
    sharedExpenses: todp(sharedE),
    totalProfit,
    ownersRevenue,
    ownerBorneExpenses,
    ownerPerUnitProfit,
    ownerExclusiveExpenses,
    nettOwnersProfit,
    perUnitRevenue,
    youEarn,
  };
};

export const formatMoney = value => {
  const n = Number(value) || 0;
  return n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const nightsBetween = (start, end) => {
  if (!start || !end) return 0;
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000)));
};

const isFreeStayBooking = row => {
  const type = String((row && (row.bookingType || row.source)) || '').toLowerCase();
  return type === '4' || type.replace(/\s+/g, '') === 'freestay' || type.includes('free stay');
};

export const freeStayFromReport = (report, reservations) => {
  const existing = (report && report.freeStay) || {};
  const usedFromRows = (reservations || []).reduce((sum, row) => {
    if (!isFreeStayBooking(row)) return sum;
    return sum + nightsBetween(row.checkInDate, row.checkOutDate);
  }, 0);
  const storedUsed = existing.used != null && existing.used !== '' ? Number(existing.used) || 0 : 0;
  const used = Math.max(storedUsed, usedFromRows);
  const cap =
    Number(report && report.unitData && report.unitData.maxFreeStay) ||
    Number(report && report.maxFreeStay) ||
    0;
  if (cap > 0) return { used, balance: Math.max(0, cap - used) };
  return { used, balance: Number(existing.balance) || 0 };
};

export const resolveUnitAvailableDays = report => {
  const src = report || {};
  if (src.unitAvailableDays != null && src.unitAvailableDays !== '') {
    return Number(src.unitAvailableDays) || 0;
  }
  const units = (src.revenue && src.revenue.units) || [];
  const match = units.find(item => item && String(item.unitId) === String(src.unit || src.unitId));
  if (match) return Number(match.availableDays) || 0;
  if (units.length === 1) return Number(units[0].availableDays) || 0;
  return 0;
};

export const resolveTotalAvailableDays = report => {
  const src = report || {};
  if (src.totalAvailableDays != null && src.totalAvailableDays !== '') {
    return Number(src.totalAvailableDays) || 0;
  }
  const units = (src.revenue && src.revenue.units) || [];
  return units.reduce((acc, item) => acc + (Number(item && item.availableDays) || 0), 0);
};

const contributionRows = (report, unitId, groupingType) => {
  const revenue = report.revenue || {};
  const rows = revenue.reservationContributions || [];
  return rows.filter(c => {
    if (!c) return false;
    const status = String(c.status || '').toLowerCase();
    if (status === 'cancelled' || status === 'deleted') return false;
    if (groupingType === 'individual' && unitId && c.unitId && String(c.unitId) !== String(unitId)) {
      return false;
    }
    return true;
  });
};

export const mapReportToCard = report => {
  const pkg = report.servicePackageData || {};
  const unitData = report.unitData || {};
  const currency = report.currency || unitData.currency || 'MYR';
  const amount = Number(report.amountTransferToOwner) || 0;
  const units = pkg.units || [];
  const ownerShareRaw =
    report.effectiveOwnerSharePercentage != null ? report.effectiveOwnerSharePercentage : pkg.ownerSharePercentage;
  const ownedByData = report.ownedByData || {};
  const populatedOwner = report.ownedBy && typeof report.ownedBy === 'object' ? report.ownedBy : null;
  const ownerName =
    report.ownerName ||
    ownedByData.name ||
    ownedByData.fullName ||
    (populatedOwner && (populatedOwner.name || [populatedOwner.firstName, populatedOwner.lastName].filter(Boolean).join(' '))) ||
    '';
  const groupingType = pkg.groupingType === 'individual' ? 'individual' : 'pool';
  const reservations = contributionRows(report, report.unit, groupingType);
  return {
    id: report._id,
    unitId: report.unit,
    year: report.year,
    month: report.month,
    propertyName: report.propertyName || '-',
    unitName: report.unitName || unitData.name || String(report.unit || '-'),
    currency,
    amountTransferToOwner: amount,
    packageName: report.servicePackageName || pkg.name || '-',
    packageUnitCount: units.length,
    packageType: report.packageType || pkg.packageType || 'profit-sharing',
    groupingType,
    shareType: pkg.shareType || report.shareType || 'share-by-profit',
    shareProfitBySqft: groupingType === 'pool' && !!pkg.shareProfitBySqft,
    ownerShare: Number(ownerShareRaw) || 0,
    totalRevenue: Number(report.totalRevenue) || 0,
    totalExpenses: Number(report.totalExpenses) || 0,
    ownerPortion: Number(report.ownerPortion) || 0,
    payoutToOwnerPerUnit:
      Number(report.payoutToOwnerPerUnit != null ? report.payoutToOwnerPerUnit : report.ownerPortion) || 0,
    unitAvailableDays: resolveUnitAvailableDays(report),
    totalAvailableDays: resolveTotalAvailableDays(report),
    installmentDue: Number(report.installmentDue) || 0,
    installmentDeducted: Number(report.installmentDeducted) || 0,
    installmentCarryOut: Number(report.installmentCarryOut) || 0,
    installmentItems: Array.isArray(report.installmentItems) ? report.installmentItems : [],
    paymentStatus: report.paymentStatus || '',
    auditStatus: report.auditStatus || 'unadited',
    ownerName: ownerName,
    netProfit: Number(report.netProfit) || 0,
    startDay: pkg.startDay || 1,
    unitSqft: report.unitSqft != null ? report.unitSqft : unitData.sqft || unitData.squareFeet,
    totalPoolSqft: report.totalPoolSqft != null ? report.totalPoolSqft : 0,
    protection: (unitData && unitData.protection) || {},
    expenseItems: expenseItemsFromReport(report.expenses, {
      unitId: report.unit,
      groupingType,
      expensesSetting: pkg.expensesSetting,
    }),
    reservations,
    freeStay: freeStayFromReport(report, reservations),
  };
};
